import { ActionIcon, Modal, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconUpload } from "@tabler/icons-react";
import { useState } from "react";

import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { uploadImage } from "api/image.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_UPLOAD_TITLE,
} from "common/constants";
import { CmsPage } from "components/editor/CmsPage";
import { ImageUploader } from "components/ImageUploader";
import ProductCards from "components/products/ProductCards";
import { useProductStore } from "stores/useProductStore";
import { useSessionStore } from "stores/userSessionStore";
import { COLORS } from "styles/colors";
import { HomeFeatureWrapper, MainBodyWrapper } from "./Home.styles";

const Home = () => {
  const { isAdmin, isGlobalAdmin, isCustomer, isLoggedIn } = useSessionStore();
  const getTopSelling = useProductStore((state) => state.getTopSelling);
  const topSelling = getTopSelling();

  const getFeatured = useProductStore((state) => state.getFeatured);
  const featured = getFeatured();

  const [open, setOpen] = useState(false);
  const closeModal = () => setOpen(false);

  return (
    <MainBodyWrapper>
      <HomeFeatureWrapper>
        {isAdmin && (
          <Tooltip label="Upload new image" position="top" withArrow>
            <ActionIcon
              className="home-feature-upload-icon"
              variant="light"
              radius="xl"
              onClick={() => setOpen(true)}
              aria-label="Change Image"
              title="Change Image"
              size="lg"
            >
              <IconUpload size={24} color={COLORS.colorRed} />
            </ActionIcon>
          </Tooltip>
        )}
      </HomeFeatureWrapper>

      <MainBodyWrapper>
        <ProductCards
          products={featured}
          title="featured products"
          span={{ base: 12, md: 3, sm: 6 }}
        />
        <CmsPage pageTitle={"home2"} showBorder></CmsPage>
        <ProductCards products={topSelling} title="top sellers" />
        <Modal opened={open} onClose={closeModal} title="Upload an Image">
          <ImageUploader
            multiple={false}
            acceptedTypes={["image/jpeg"]}
            comments="Note: The filename will be overridden to '3.jpg'.  The recommended image size for this image is 2000px x 588px."
            overrideFilename={(file: File) => `3.jpg`}
            onUpload={async (files: File[], paths?: string[]) => {
              const file = files[0]; // only one file since multiple={false}
              const fullPath = paths?.[0]; // e.g. '/images/3.jpg'
              const overrideFilename = fullPath?.split("/").pop(); // returns '3.jpg'

              if (!file) {
                notifications.show({
                  title: MSG_DEFAULT_UPLOAD_TITLE,
                  message: "No file selected for upload. Please select a file.",
                  position: MantineNotificationPosition.TopCenter,
                  color: MantineNotificationColor.Warn,
                });
                console.error("No file selected for upload.");
                return;
              }
              // You might extract 'isDetailed' from paths
              const isDetailed = fullPath?.includes("/d") ?? false;
              try {
                const response = await safeApiCall(
                  uploadImage(file, isDetailed, overrideFilename),
                );
                notifications.show({
                  title: MSG_DEFAULT_UPLOAD_TITLE,
                  message: `Image uploaded successfully! ${response.data.location}`,
                  position: MantineNotificationPosition.TopCenter,
                  color: MantineNotificationColor.Success,
                });
              } catch (error) {
                logError(error);
              } finally {
                closeModal();
              }
            }}
          />
        </Modal>
      </MainBodyWrapper>
    </MainBodyWrapper>
  );
};

export default Home;
// This component serves as the home page of the application.
// It displays a welcome message and a brief description of the application.
